import { GradientBorder, IconBox, VisionCard } from "@/components/VisionUI"
import { Button } from "@/components/ui/button"
import { Field } from "@/components/ui/field"
import { ReactIcon } from "@/components/ui/icon"
import { InputGroup } from "@/components/ui/input-group"
import { Container, Flex, Heading, Input, Text, VStack } from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FaEye, FaEyeSlash, FaKey } from "react-icons/fa"
import { useBoolean } from "usehooks-ts"
import { type ApiError, OpenAPI } from "../client"

interface ResetPassword {
  token: string
  new_password: string
}
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { effects } from "@/theme"
import { isLoggedIn } from "../hooks/useAuth"
import useCustomToast from "../hooks/useCustomToast"
import { confirmPasswordRules, handleError, passwordRules } from "../utils"

interface NewPassword extends ResetPassword {
  confirm_password: string
}

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/",
      })
    }
  },
})

function ResetPasswordPage() {
  const { t } = useTranslation("auth")
  const { value: show, toggle } = useBoolean()
  const { showSuccessToast } = useCustomToast()
  const sc = useSemanticColors()
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<NewPassword>({
    mode: "onBlur",
    criteriaMode: "all",
  })

  const resetPassword = async (data: ResetPassword) => {
    // POST /api/v1/reset-password/. The backend route is
    // include_in_schema=False, so the generated client no longer exposes
    // LoginService.resetPassword. Call it directly, mirroring login.tsx.
    const base = (OpenAPI.BASE || "").replace(/\/$/, "")
    const res = await fetch(`${base}/api/v1/reset-password/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: data.token,
        new_password: data.new_password,
      }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error((body?.detail as string) || "Password reset failed")
    }
  }

  const mutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      showSuccessToast(t("messages.passwordUpdated"))
      navigate({ to: "/login" })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const onSubmit: SubmitHandler<NewPassword> = async (data) => {
    mutation.mutate(data)
  }

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={sc.pageBg}
      backgroundImage={effects.gradients.bgGradient}
      backgroundAttachment="fixed"
      p={{ base: 3, md: 4 }}
    >
      <Container maxW="md" px={0}>
        <GradientBorder borderRadius="30px" p={0}>
          <VisionCard
            variant="elevated"
            p={{ base: 5, md: 8 }}
            borderRadius="30px"
          >
            <VStack gap={6} as="form" onSubmit={handleSubmit(onSubmit)}>
              <Flex direction="column" align="center" gap={4}>
                <IconBox variant="gradient" size="xl">
                  <FaKey fontSize="36px" />
                </IconBox>
                <Heading size="lg" color={sc.heading}>
                  {t("reset.title")}
                </Heading>
                <Text color={sc.textMuted} textAlign="center">
                  {t("reset.subtitle")}
                </Text>
              </Flex>

              <VStack w="100%" gap={4}>
                <Input {...register("token")} type="hidden" />

                <Field
                  required
                  invalid={!!errors.new_password}
                  errorText={errors.new_password?.message}
                >
                  <InputGroup
                    width="100%"
                    endElement={
                      <ReactIcon
                        icon={show ? FaEyeSlash : FaEye}
                        cursor="pointer"
                        onClick={toggle}
                        aria-label={
                          show ? t("hidePassword") : t("showPassword")
                        }
                        color={sc.textMuted}
                        _hover={{ color: sc.heading }}
                      />
                    }
                  >
                    <Input
                      {...register("new_password", passwordRules())}
                      type={show ? "text" : "password"}
                      placeholder={t("reset.newPassword")}
                      size="lg"
                      bg="bg.glass"
                      borderColor="border.default"
                      _hover={{
                        borderColor: "border.muted",
                      }}
                      _focus={{
                        borderColor: "brand.200",
                        boxShadow: "0 0 0 1px var(--vision-colors-brand-200)",
                      }}
                      required
                    />
                  </InputGroup>
                </Field>

                <Field
                  required
                  invalid={!!errors.confirm_password}
                  errorText={errors.confirm_password?.message}
                >
                  <InputGroup
                    width="100%"
                    endElement={
                      <ReactIcon
                        icon={show ? FaEyeSlash : FaEye}
                        cursor="pointer"
                        onClick={toggle}
                        aria-label={
                          show ? t("hidePassword") : t("showPassword")
                        }
                        color={sc.textMuted}
                        _hover={{ color: sc.heading }}
                      />
                    }
                  >
                    <Input
                      {...register(
                        "confirm_password",
                        confirmPasswordRules(getValues),
                      )}
                      type={show ? "text" : "password"}
                      placeholder={t("reset.confirmPassword")}
                      size="lg"
                      bg="bg.glass"
                      borderColor="border.default"
                      _hover={{
                        borderColor: "border.muted",
                      }}
                      _focus={{
                        borderColor: "brand.200",
                        boxShadow: "0 0 0 1px var(--vision-colors-brand-200)",
                      }}
                      required
                    />
                  </InputGroup>
                </Field>

                <Button
                  type="submit"
                  size="lg"
                  w="100%"
                  bg="brand.200"
                  color="white"
                  _hover={{
                    bg: "brand.300",
                    transform: "translateY(-2px)",
                    boxShadow: "0px 8px 24px rgba(52, 161, 221, 0.5)",
                  }}
                >
                  {t("reset.submit")}
                </Button>
              </VStack>
            </VStack>
          </VisionCard>
        </GradientBorder>
      </Container>
    </Flex>
  )
}
